import { useEffect, useRef, useState } from "react";
import { FaBell } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import client from "../api/client";
import { parseJwtPayload } from "../utils/authSession";
import "./AdminNotificationBell.css";

const POLL_INTERVAL_MS = 15000;

const isAdmin = () => String(parseJwtPayload(localStorage.getItem("token"))?.role || "").toLowerCase() === "admin";

const formatTime = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

// Browsers may reject sound until a user gesture has unlocked audio. A failed
// attempt is deliberately silent; the visible unread badge remains the fallback.
const playNotificationSound = async () => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const context = new AudioContext();
    await context.resume();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.frequency.value = 880;
    gain.gain.setValueAtTime(0.045, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.14);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.15);
    oscillator.addEventListener("ended", () => context.close().catch(() => {}), { once: true });
  } catch {
    // Autoplay restriction or an unavailable audio output: notification stays visible.
  }
};

export default function AdminNotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const initialLoad = useRef(true);
  const previousUnreadCount = useRef(0);
  const bellRef = useRef(null);
  const drag = useRef(null);
  const suppressClick = useRef(false);
  const [position, setPosition] = useState(null);

  useEffect(() => {
    if (!isAdmin()) return undefined;
    let active = true;
    const load = async () => {
      try {
        setLoadError("");
        const response = await client.get("/admin-notifications", { params: { limit: 30 } });
        if (!active) return;
        const nextUnread = Number(response.data?.unreadCount || 0);
        if (!initialLoad.current && nextUnread > previousUnreadCount.current) void playNotificationSound();
        initialLoad.current = false;
        previousUnreadCount.current = nextUnread;
        setUnreadCount(nextUnread);
        setNotifications(Array.isArray(response.data?.notifications) ? response.data.notifications : []);
      } catch (error) {
        // A transient API error should not clear already-visible notifications.
        console.error("Load admin notifications failed:", error);
        if (active) setLoadError("Notifications could not be loaded. Please try again.");
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    const interval = window.setInterval(load, POLL_INTERVAL_MS);
    return () => { active = false; window.clearInterval(interval); };
  }, []);

  useEffect(() => {
    if (!position) return undefined;
    const keepBellVisible = () => {
      const rect = bellRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPosition((currentPosition) => currentPosition && {
        left: Math.max(0, Math.min(window.innerWidth - rect.width, currentPosition.left)),
        top: Math.max(0, Math.min(window.innerHeight - rect.height, currentPosition.top)),
      });
    };
    window.addEventListener("resize", keepBellVisible);
    return () => window.removeEventListener("resize", keepBellVisible);
  }, [position]);

  if (!isAdmin()) return null;

  const selectNotification = async (notification) => {
    if (!notification.read) {
      try {
        await client.patch(`/admin-notifications/${notification.id}/read`);
        setNotifications((items) => items.map((item) => item.id === notification.id ? { ...item, read: true, read_at: new Date().toISOString() } : item));
        setUnreadCount((count) => Math.max(0, count - 1));
        previousUnreadCount.current = Math.max(0, previousUnreadCount.current - 1);
      } catch (error) {
        console.error("Mark notification read failed:", error);
      }
    }
    setOpen(false);
    // Only server-generated destination paths are stored. No arbitrary URL is followed.
    if (String(notification.destination_path || "").startsWith("/help-desk")) navigate(notification.destination_path);
  };

  const visibleNotifications = notifications.slice(0, 15);
  const viewAllNotifications = () => {
    setOpen(false);
    navigate("/notifications");
  };

  const startDrag = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    const rect = bellRef.current?.getBoundingClientRect();
    if (!rect) return;
    drag.current = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, left: rect.left, top: rect.top, width: rect.width, height: rect.height, moved: false };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveDrag = (event) => {
    const currentDrag = drag.current;
    if (!currentDrag || currentDrag.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - currentDrag.startX;
    const deltaY = event.clientY - currentDrag.startY;
    if (!currentDrag.moved && Math.hypot(deltaX, deltaY) < 4) return;
    currentDrag.moved = true;
    suppressClick.current = true;
    event.preventDefault();
    setPosition({
      left: Math.max(0, Math.min(window.innerWidth - currentDrag.width, currentDrag.left + deltaX)),
      top: Math.max(0, Math.min(window.innerHeight - currentDrag.height, currentDrag.top + deltaY)),
    });
  };

  const endDrag = (event) => {
    if (drag.current?.pointerId !== event.pointerId) return;
    drag.current = null;
  };

  const toggleNotifications = (event) => {
    if (suppressClick.current) {
      suppressClick.current = false;
      event.preventDefault();
      return;
    }
    setOpen((value) => !value);
  };

  return (
    <div className="admin-notifications" style={position ? { left: position.left, top: position.top, right: "auto" } : undefined}>
      <button ref={bellRef} type="button" className="admin-notification-bell" onPointerDown={startDrag} onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={endDrag} onClick={toggleNotifications} aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ""}`} aria-expanded={open}>
        <FaBell />
        {unreadCount > 0 && <span className="admin-notification-count">{unreadCount > 99 ? "99+" : unreadCount}</span>}
      </button>
      {open && <section className="admin-notification-menu" aria-label="Notifications">
        <header><span>Notifications</span>{unreadCount > 0 && <small>{unreadCount} unread</small>}</header>
        <div className="admin-notification-list">
          {loading ? <p className="admin-notification-empty">Loading notifications...</p>
            : loadError && notifications.length === 0 ? <p className="admin-notification-error">{loadError}</p>
              : notifications.length === 0 ? <p className="admin-notification-empty">No notifications yet.</p>
                : visibleNotifications.map((notification) => (
                  <button type="button" key={notification.id} className={`admin-notification-item${notification.read ? "" : " unread"}`} onClick={() => selectNotification(notification)}>
                    <span className="admin-notification-item-heading"><strong>{notification.title}</strong><time>{formatTime(notification.created_at)}</time></span>
                    <span className="admin-notification-message">{notification.message}</span>
                  </button>
                ))}
        </div>
        {notifications.length > 5 && <button type="button" className="admin-notification-view-all" onClick={viewAllNotifications}>View All Notifications</button>}
      </section>}
    </div>
  );
}
